package com.arena_web.core.viewmodel

import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.*
import com.arena_web.ui.localization.uploadErrorMessage
import com.arena_web.data.websocket.ChatWsEvent
import com.arena_web.data.websocket.ChatWebSocketClient
import com.arena_web.data.websocket.UserWebSocketClient
import com.arena_web.data.websocket.UserWsEvent
import com.arena_web.domain.repositories.ChatRepository
import com.arena_web.domain.repositories.FollowRepository
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val FOLLOWING_PAGE_SIZE = 20
private const val FOLLOWING_SEARCH_DEBOUNCE_MS = 300L
private const val CHAT_LIST_PAGE_SIZE = 20
private const val CHAT_LIST_SEARCH_DEBOUNCE_MS = 300L

data class PendingFile(
    val bytes: ByteArray,
    val name: String,
    val contentType: String,
    val isImage: Boolean = contentType.startsWith("image/")
)

data class ChatUiState(
    val chats: List<ChatListItemDto> = emptyList(),
    val selectedChatId: String? = null,
    val selectedChat: ChatDto? = null,
    val messages: List<MessageDto> = emptyList(),
    val messageText: String = "",
    val typingUsers: Map<String, String> = emptyMap(),
    val onlineUserIds: Set<String> = emptySet(),
    val chatsLoading: Boolean = false,
    // Server-side pagination + debounced search for the chat list. The
    // panel scrolls indefinitely, calling `loadMoreChats()` once near the
    // bottom; search queries reset to page 1 with a 300 ms debounce.
    val chatsPage: Int = 0,
    val chatsHasMore: Boolean = true,
    val chatsLoadingMore: Boolean = false,
    val chatsSearchQuery: String = "",
    val chatsTotal: Long = 0,
    val messagesLoading: Boolean = false,
    val sendingMessage: Boolean = false,
    val error: String? = null,
    val currentUserId: String? = null,
    val currentPage: Int = 0,
    val hasMoreMessages: Boolean = true,
    val loadingMore: Boolean = false,
    // New chat dialog
    val showNewChatDialog: Boolean = false,
    val following: List<FollowUserDto> = emptyList(),
    val followingLoading: Boolean = false,
    // Pagination state for the DM picker's following list. `followingPage`
    // is the latest page successfully appended (0 = nothing loaded yet);
    // `followingHasMore` flips to false once we've consumed the last page.
    val followingPage: Int = 0,
    val followingHasMore: Boolean = true,
    val followingLoadingMore: Boolean = false,
    // Debounced server-side query string applied to /follow/me/following.
    val followingSearchQuery: String = "",
    val creatingChat: Boolean = false,
    // Edit group + invite members (group chats). Both reuse the `following`
    // picker state above; only one of the two is open at a time.
    val showEditChatDialog: Boolean = false,
    val savingGroup: Boolean = false,
    val showInviteDialog: Boolean = false,
    val invitingMembers: Boolean = false,
    // Member pending removal — drives the remove-confirmation popup.
    val memberPendingRemoval: ChatMemberDto? = null,
    // Message search
    val showMessageSearch: Boolean = false,
    val messageSearchQuery: String = "",
    val messageSearchResults: List<MessageDto> = emptyList(),
    val messageSearchLoading: Boolean = false,
    // Edit message
    val editingMessage: MessageDto? = null,
    // Chat actions
    val actionInProgress: Boolean = false,
    // Chat info
    val showChatMenu: Boolean = false,
    // Chat info panel
    val showChatInfo: Boolean = false,
    // Pinned message
    val pinnedMessage: MessageDto? = null,
    // Pending direct chat (lazy creation — not yet created on server)
    val pendingDirectUserId: String? = null,
    // Reply
    val replyingToMessage: MessageDto? = null,
    // File upload
    val pendingFile: PendingFile? = null,
    val uploadingFile: Boolean = false
)

class ChatViewModel(
    private val chatRepository: ChatRepository,
    private val followRepository: FollowRepository? = null,
    private val userWsClient: UserWebSocketClient? = null
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ChatUiState())
    val uiState: StateFlow<ChatUiState> = _uiState.asStateFlow()

    private val wsClient = ChatWebSocketClient()
    private var wsCollectorJob: Job? = null
    private var userWsCollectorJob: Job? = null
    private val typingClearJobs = mutableMapOf<String, Job>()

    override fun onCreate() {
        super.onCreate()
        _uiState.update { it.copy(currentUserId = Storage.get(StorageKeys.USER_ID)) }
        loadChats()
        startUserWsCollector()
    }

    private fun startUserWsCollector() {
        userWsCollectorJob = viewModelScope.launch {
            userWsClient?.events?.collect { event ->
                when (event) {
                    is UserWsEvent.ChatListUpdate -> refreshChatList()
                    is UserWsEvent.OnlineUsersList -> {
                        _uiState.update { it.copy(onlineUserIds = event.userIds) }
                    }
                    is UserWsEvent.UserOnline -> {
                        _uiState.update { it.copy(onlineUserIds = it.onlineUserIds + event.userId) }
                    }
                    is UserWsEvent.UserOffline -> {
                        _uiState.update { it.copy(onlineUserIds = it.onlineUserIds - event.userId) }
                    }
                    else -> {}
                }
            }
        }
    }

    override fun onDispose() {
        wsClient.disconnect()
        wsCollectorJob?.cancel()
        userWsCollectorJob?.cancel()
        typingClearJobs.values.forEach { it.cancel() }
        super.onDispose()
    }

    // ── Chat list ──

    private var chatsSearchJob: Job? = null

    /** Loads page 1 of the chat list using the currently-active search. WS
     *  list updates and post-action refreshes flow through here, so the
     *  visible list always reflects the latest server state for the active
     *  query.
     */
    fun loadChats() {
        viewModelScope.launch {
            val search = _uiState.value.chatsSearchQuery
            _uiState.update { it.copy(chatsLoading = it.chats.isEmpty(), error = null) }
            chatRepository.getChats(chatListParams(page = 1, search = search))
                .onSuccess { page ->
                    if (_uiState.value.chatsSearchQuery != search) return@onSuccess
                    _uiState.update {
                        it.copy(
                            chats = page.items,
                            chatsLoading = false,
                            chatsPage = page.page,
                            chatsHasMore = page.page < page.totalPages,
                            chatsTotal = page.totalItems,
                        )
                    }
                }
                .onFailure { e ->
                    if (_uiState.value.chatsSearchQuery != search) return@onFailure
                    _uiState.update {
                        it.copy(
                            error = e.message,
                            chatsLoading = false,
                            chatsHasMore = false,
                        )
                    }
                }
        }
    }

    fun loadMoreChats() {
        val state = _uiState.value
        if (state.chatsLoading || state.chatsLoadingMore || !state.chatsHasMore) return
        val nextPage = state.chatsPage + 1
        val search = state.chatsSearchQuery
        _uiState.update { it.copy(chatsLoadingMore = true) }
        viewModelScope.launch {
            chatRepository.getChats(chatListParams(page = nextPage, search = search))
                .onSuccess { page ->
                    if (_uiState.value.chatsSearchQuery != search) return@onSuccess
                    val existing = _uiState.value.chats.mapTo(mutableSetOf()) { it.id }
                    val merged = _uiState.value.chats + page.items.filter { it.id !in existing }
                    _uiState.update {
                        it.copy(
                            chats = merged,
                            chatsLoadingMore = false,
                            chatsPage = page.page,
                            chatsHasMore = page.page < page.totalPages,
                            chatsTotal = page.totalItems,
                        )
                    }
                }
                .onFailure { e ->
                    if (_uiState.value.chatsSearchQuery != search) return@onFailure
                    _uiState.update {
                        it.copy(error = e.message, chatsLoadingMore = false)
                    }
                }
        }
    }

    /** Debounced server-side search for the chat list. Each keystroke
     *  cancels the pending request and waits [CHAT_LIST_SEARCH_DEBOUNCE_MS]
     *  before firing a fresh page-1 fetch.
     */
    fun setChatsSearchQuery(query: String) {
        if (query == _uiState.value.chatsSearchQuery) return
        _uiState.update { it.copy(chatsSearchQuery = query, chatsLoading = true) }
        chatsSearchJob?.cancel()
        chatsSearchJob = viewModelScope.launch {
            delay(CHAT_LIST_SEARCH_DEBOUNCE_MS)
            loadChats()
        }
    }

    private fun chatListParams(page: Int, search: String): Map<String, String> {
        val base = mapOf("page" to "$page", "size" to "$CHAT_LIST_PAGE_SIZE")
        return if (search.isBlank()) base else base + ("search" to search.trim())
    }

    /**
     * Clears the current selection (mobile back-button — closes the room
     * screen without leaving the chat). Desktop callers never need this
     * because the room is always visible next to the list.
     */
    fun closeSelectedChat() {
        _uiState.update {
            it.copy(
                selectedChatId = null,
                selectedChat = null,
                messages = emptyList(),
                typingUsers = emptyMap(),
                messageText = "",
                replyingToMessage = null,
                editingMessage = null,
                pendingFile = null,
            )
        }
    }

    fun selectChat(chatId: String) {
        if (_uiState.value.selectedChatId == chatId) return

        _uiState.update {
            it.copy(
                selectedChatId = chatId,
                messagesLoading = true,
                typingUsers = emptyMap(),
                currentPage = 0,
                hasMoreMessages = true,
                error = null,
                showMessageSearch = false,
                messageSearchQuery = "",
                messageSearchResults = emptyList(),
                editingMessage = null,
                showChatMenu = false,
                showChatInfo = false,
                replyingToMessage = null,
                pendingFile = null,
                uploadingFile = false
            )
        }

        viewModelScope.launch {
            val chatResult = chatRepository.getChat(chatId)
            val messagesResult = chatRepository.getMessages(chatId, mapOf("page" to "0", "size" to "50"))

            chatResult.onSuccess { chat ->
                messagesResult.onSuccess { messagesPage ->
                    _uiState.update {
                        it.copy(
                            selectedChat = chat,
                            messages = messagesPage.messages.reversed(),
                            messagesLoading = false,
                            currentPage = 0,
                            hasMoreMessages = messagesPage.messages.size >= 50
                        )
                    }
                }.onFailure { e ->
                    _uiState.update { it.copy(error = e.message, messagesLoading = false) }
                }
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, messagesLoading = false) }
            }
        }

        loadPinnedMessage(chatId)
        connectWebSocket(chatId)
        markAsRead(chatId)
    }

    // ── Messages ──

    private fun loadMessages(chatId: String, page: Int) {
        viewModelScope.launch {
            val params = mapOf("page" to page.toString(), "size" to "50")
            chatRepository.getMessages(chatId, params)
                .onSuccess { messagesPage ->
                    _uiState.update { state ->
                        val newMessages = messagesPage.messages.reversed()
                        val allMessages = if (page == 0) newMessages
                        else newMessages + state.messages

                        state.copy(
                            messages = allMessages,
                            messagesLoading = false,
                            loadingMore = false,
                            currentPage = page,
                            hasMoreMessages = messagesPage.messages.size >= 50
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(error = e.message, messagesLoading = false, loadingMore = false)
                    }
                }
        }
    }

    fun loadMoreMessages() {
        val state = _uiState.value
        if (state.loadingMore || !state.hasMoreMessages || state.selectedChatId == null) return
        _uiState.update { it.copy(loadingMore = true) }
        loadMessages(state.selectedChatId, state.currentPage + 1)
    }

    fun updateMessageText(text: String) {
        _uiState.update { it.copy(messageText = text) }
    }

    fun sendMessage() {
        val state = _uiState.value
        val text = state.messageText.trim()
        logger.debug("sendMessage() called: text.length=${text.length} selectedChatId=${state.selectedChatId} pendingDirectUserId=${state.pendingDirectUserId}")
        if (text.isEmpty()) {
            logger.debug("sendMessage() early-return: text is empty")
            return
        }
        AnalyticsTracker.trackClick("send_message", screen = "chat")

        // Lazy creation: if there's a pending direct chat, create it first
        val pendingUserId = state.pendingDirectUserId
        if (pendingUserId != null && state.selectedChatId == null) {
            _uiState.update { it.copy(messageText = "", sendingMessage = true) }
            viewModelScope.launch {
                chatRepository.createDirectChat(CreateDirectChatRequest(targetUserId = pendingUserId))
                    .onSuccess { chat ->
                        _uiState.update { it.copy(pendingDirectUserId = null) }
                        refreshChatList()
                        selectChat(chat.id)
                        // Now send the message in the newly created chat
                        chatRepository.sendMessage(chat.id, SendMessageRequest(content = text))
                            .onSuccess { message ->
                                _uiState.update { s ->
                                    s.copy(messages = s.messages + message, sendingMessage = false)
                                }
                                refreshChatList()
                            }
                            .onFailure { e ->
                                _uiState.update { it.copy(error = e.message, sendingMessage = false) }
                            }
                    }
                    .onFailure { e ->
                        _uiState.update { it.copy(error = e.message, sendingMessage = false) }
                    }
            }
            return
        }

        val chatId = state.selectedChatId
        if (chatId == null) {
            logger.debug("sendMessage() early-return: selectedChatId is null")
            return
        }
        val replyToId = state.replyingToMessage?.id
        _uiState.update { it.copy(messageText = "", sendingMessage = true, replyingToMessage = null) }
        logger.debug("sendMessage() launching coroutine: chatId=$chatId text='$text'")

        viewModelScope.launch {
            logger.debug("sendMessage() coroutine started, calling chatRepository.sendMessage()")
            chatRepository.sendMessage(chatId, SendMessageRequest(content = text, replyToMessageId = replyToId))
                .onSuccess { message ->
                    logger.debug("sendMessage() success: message.id=${message.id}")
                    _uiState.update { s ->
                        s.copy(messages = s.messages + message, sendingMessage = false)
                    }
                    refreshChatList()
                }
                .onFailure { e ->
                    logger.debug("sendMessage() failure: ${e::class.simpleName}: ${e.message}")
                    _uiState.update { it.copy(error = e.message, sendingMessage = false) }
                }
        }
    }

    /**
     * Start a direct chat with a user from their profile.
     * If a DM already exists, selects it. Otherwise, creates a pending state
     * and the actual chat is created lazily when the first message is sent.
     */
    fun startDirectChatWithUser(targetUserId: String, userName: String? = null, userAvatar: String? = null) {
        viewModelScope.launch {
            // Wait for chats to load if still loading
            val currentState = _uiState.value
            val chats = if (currentState.chatsLoading) {
                // Chats are still loading, fetch page 1 (search-free) just to
                // sniff for an existing direct chat with this user.
                chatRepository.getChats(mapOf("page" to "1", "size" to "$CHAT_LIST_PAGE_SIZE"))
                    .getOrNull()?.items ?: emptyList()
            } else {
                currentState.chats
            }

            // Check if a DM already exists with this user
            // Direct chats have type "DIRECT" — we need to check the detail
            val existingChat = findExistingDirectChat(chats, targetUserId)

            if (existingChat != null) {
                selectChat(existingChat.id)
            } else {
                // Set up pending state — show empty chat room, create on first message
                _uiState.update {
                    it.copy(
                        pendingDirectUserId = targetUserId,
                        selectedChatId = null,
                        selectedChat = createPendingChatDto(targetUserId, userName, userAvatar),
                        messages = emptyList(),
                        messagesLoading = false
                    )
                }
            }
        }
    }

    private fun findExistingDirectChat(chats: List<ChatListItemDto>, targetUserId: String): ChatListItemDto? {
        return chats.firstOrNull {
            it.type.uppercase() == "DIRECT" && it.otherUserId == targetUserId
        }
    }

    private fun createPendingChatDto(targetUserId: String, userName: String? = null, userAvatar: String? = null): ChatDto {
        // Create a minimal ChatDto for the pending UI state
        return ChatDto(
            id = "pending",
            type = "DIRECT",
            name = userName,
            imageUrl = userAvatar,
            privacy = "PRIVATE",
            creatorId = _uiState.value.currentUserId ?: "",
            members = emptyList(),
            createdAt = "",
            updatedAt = ""
        )
    }

    private fun loadPinnedMessage(chatId: String) {
        viewModelScope.launch {
            chatRepository.getPinnedMessages(chatId)
                .onSuccess { pinned ->
                    _uiState.update { it.copy(pinnedMessage = pinned.firstOrNull()) }
                }
                .onFailure {
                    _uiState.update { it.copy(pinnedMessage = null) }
                }
        }
    }

    fun toggleReaction(messageId: String, emoji: String) {
        val state = _uiState.value
        val chatId = state.selectedChatId ?: return
        val currentUserId = state.currentUserId ?: return
        val message = state.messages.find { it.id == messageId } ?: return
        val existingReaction = message.reactions.find { it.emoji == emoji }
        val alreadyReacted = existingReaction?.userIds?.contains(currentUserId) == true

        viewModelScope.launch {
            if (alreadyReacted) {
                chatRepository.removeReaction(chatId, messageId, emoji)
            } else {
                chatRepository.addReaction(chatId, messageId, AddReactionRequest(emoji = emoji))
            }
        }
    }

    // ── Reply ──

    fun startReply(message: MessageDto) {
        _uiState.update { it.copy(replyingToMessage = message) }
    }

    fun cancelReply() {
        _uiState.update { it.copy(replyingToMessage = null) }
    }

    // ── File upload ──

    fun setPendingFile(bytes: ByteArray, name: String, contentType: String) {
        _uiState.update { it.copy(pendingFile = PendingFile(bytes, name, contentType)) }
    }

    fun clearPendingFile() {
        _uiState.update { it.copy(pendingFile = null) }
    }

    fun sendMediaMessage() {
        val state = _uiState.value
        val chatId = state.selectedChatId ?: return
        val file = state.pendingFile ?: return
        _uiState.update { it.copy(uploadingFile = true) }

        viewModelScope.launch {
            chatRepository.sendMediaMessage(chatId, file.bytes, file.name, file.contentType)
                .onSuccess { message ->
                    _uiState.update { s ->
                        s.copy(
                            messages = s.messages + message,
                            pendingFile = null,
                            uploadingFile = false
                        )
                    }
                    refreshChatList()
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = uploadErrorMessage(e), uploadingFile = false) }
                }
        }
    }

    // ── Pin ──

    fun pinMessage(messageId: String) {
        val chatId = _uiState.value.selectedChatId ?: return
        viewModelScope.launch {
            chatRepository.togglePin(chatId, messageId)
                .onSuccess { updatedMsg ->
                    _uiState.update { state ->
                        state.copy(
                            messages = state.messages.map { if (it.id == updatedMsg.id) updatedMsg else it },
                            pinnedMessage = if (updatedMsg.isPinned) updatedMsg else null
                        )
                    }
                }
        }
    }

    fun deleteMessage(messageId: String) {
        val chatId = _uiState.value.selectedChatId ?: return
        viewModelScope.launch {
            chatRepository.deleteMessage(chatId, messageId)
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(messages = state.messages.filter { it.id != messageId })
                    }
                    refreshChatList()
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message) }
                }
        }
    }

    fun startEditMessage(message: MessageDto) {
        _uiState.update { it.copy(editingMessage = message, messageText = message.content) }
    }

    fun cancelEditMessage() {
        _uiState.update { it.copy(editingMessage = null, messageText = "") }
    }

    fun submitEditMessage() {
        val state = _uiState.value
        val chatId = state.selectedChatId ?: return
        val editing = state.editingMessage ?: return
        val newContent = state.messageText.trim()
        if (newContent.isEmpty() || newContent == editing.content) {
            cancelEditMessage()
            return
        }

        _uiState.update { it.copy(actionInProgress = true) }

        viewModelScope.launch {
            chatRepository.editMessage(chatId, editing.id, EditMessageRequest(content = newContent))
                .onSuccess { updatedMsg ->
                    _uiState.update { s ->
                        s.copy(
                            messages = s.messages.map { if (it.id == updatedMsg.id) updatedMsg else it },
                            editingMessage = null,
                            messageText = "",
                            actionInProgress = false
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, actionInProgress = false) }
                }
        }
    }

    fun sendTyping() {
        wsClient.sendTyping()
    }

    // ── Create chat ──

    fun showNewChatDialog() {
        _uiState.update {
            it.copy(
                showNewChatDialog = true,
                followingLoading = true,
                following = emptyList(),
                followingPage = 0,
                followingHasMore = true,
                followingLoadingMore = false,
                followingSearchQuery = "",
            )
        }
        loadFollowing()
    }

    fun hideNewChatDialog() {
        followingSearchJob?.cancel()
        followingSearchJob = null
        _uiState.update {
            it.copy(
                showNewChatDialog = false,
                following = emptyList(),
                followingPage = 0,
                followingHasMore = true,
                followingLoading = false,
                followingLoadingMore = false,
                followingSearchQuery = "",
            )
        }
    }

    private var followingSearchJob: Job? = null

    /** Debounced server-side search for the DM picker. Each keystroke
     *  cancels the pending request and waits [FOLLOWING_SEARCH_DEBOUNCE_MS]
     *  before firing a fresh page-1 fetch against /follow/me/following.
     */
    fun setFollowingSearchQuery(query: String) {
        val trimmed = query
        if (trimmed == _uiState.value.followingSearchQuery) return
        _uiState.update { it.copy(followingSearchQuery = trimmed, followingLoading = true) }
        followingSearchJob?.cancel()
        followingSearchJob = viewModelScope.launch {
            delay(FOLLOWING_SEARCH_DEBOUNCE_MS)
            loadFollowing()
        }
    }

    private fun loadFollowing() {
        viewModelScope.launch {
            // Only people I follow can be DM'd (sender must follow recipient).
            // First page of the paginated picker — subsequent pages flow
            // through loadMoreFollowing() as the user scrolls.
            val repo = followRepository
            if (repo == null) {
                _uiState.update { it.copy(followingLoading = false, followingHasMore = false) }
                return@launch
            }
            val search = _uiState.value.followingSearchQuery
            repo.getMyFollowing(followingParams(page = 1, search = search))
                .onSuccess { page ->
                    // Discard the response if the user has already moved on
                    // to a different query — keeps the visible list aligned
                    // with the input field even when requests overlap.
                    if (_uiState.value.followingSearchQuery != search) return@onSuccess
                    val available = page.items.filterAvailableForDm()
                    _uiState.update {
                        it.copy(
                            following = available,
                            followingLoading = false,
                            followingPage = page.page,
                            followingHasMore = page.page < page.totalPages,
                        )
                    }
                }
                .onFailure { e ->
                    if (_uiState.value.followingSearchQuery != search) return@onFailure
                    _uiState.update {
                        it.copy(
                            error = e.message,
                            followingLoading = false,
                            followingHasMore = false,
                        )
                    }
                }
        }
    }

    fun loadMoreFollowing() {
        val state = _uiState.value
        if (state.followingLoading || state.followingLoadingMore || !state.followingHasMore) return
        val repo = followRepository ?: return
        val nextPage = state.followingPage + 1
        val search = state.followingSearchQuery
        _uiState.update { it.copy(followingLoadingMore = true) }
        viewModelScope.launch {
            repo.getMyFollowing(followingParams(page = nextPage, search = search))
                .onSuccess { page ->
                    if (_uiState.value.followingSearchQuery != search) return@onSuccess
                    val newAvailable = page.items.filterAvailableForDm()
                    val existingIds = _uiState.value.following.mapTo(mutableSetOf()) { it.userId }
                    val merged = _uiState.value.following + newAvailable.filter { it.userId !in existingIds }
                    _uiState.update {
                        it.copy(
                            following = merged,
                            followingLoadingMore = false,
                            followingPage = page.page,
                            followingHasMore = page.page < page.totalPages,
                        )
                    }
                }
                .onFailure { e ->
                    if (_uiState.value.followingSearchQuery != search) return@onFailure
                    _uiState.update {
                        it.copy(
                            error = e.message,
                            followingLoadingMore = false,
                        )
                    }
                }
        }
    }

    private fun followingParams(page: Int, search: String): Map<String, String> {
        val base = mapOf("page" to "$page", "size" to "$FOLLOWING_PAGE_SIZE")
        return if (search.isBlank()) base else base + ("search" to search.trim())
    }

    private fun List<FollowUserDto>.filterAvailableForDm(): List<FollowUserDto> {
        // Invite picker reuses this loader but wants a different exclusion:
        // people already in the group rather than people already DM'd.
        if (_uiState.value.showInviteDialog) {
            val memberIds = _uiState.value.selectedChat?.members
                ?.mapTo(mutableSetOf()) { it.userId } ?: emptySet()
            return filter { it.userId !in memberIds }
        }
        val existingDirectUserIds = _uiState.value.chats
            .filter { it.type.uppercase() == "DIRECT" }
            .mapNotNull { it.otherUserId }
            .toSet()
        return filter { it.userId !in existingDirectUserIds }
    }

    fun createDirectChat(targetUserId: String) {
        _uiState.update { it.copy(creatingChat = true) }
        viewModelScope.launch {
            chatRepository.createDirectChat(CreateDirectChatRequest(targetUserId = targetUserId))
                .onSuccess { chat ->
                    _uiState.update { it.copy(showNewChatDialog = false, creatingChat = false) }
                    refreshChatList()
                    selectChat(chat.id)
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, creatingChat = false) }
                }
        }
    }

    fun createGroupChat(
        name: String,
        privacy: String = "PUBLIC",
        imageBytes: ByteArray? = null,
        imageFileName: String? = null,
        imageContentType: String? = null
    ) {
        _uiState.update { it.copy(creatingChat = true) }
        viewModelScope.launch {
            chatRepository.createGroupChat(CreateGroupChatRequest(name = name, privacy = privacy))
                .onSuccess { chat ->
                    // Upload avatar if provided
                    if (imageBytes != null && imageFileName != null && imageContentType != null) {
                        chatRepository.uploadChatImage(chat.id, imageBytes, imageFileName, imageContentType)
                    }
                    _uiState.update { it.copy(showNewChatDialog = false, creatingChat = false) }
                    refreshChatList()
                    selectChat(chat.id)
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, creatingChat = false) }
                }
        }
    }

    // ── Edit group ──

    fun showEditChatDialog() {
        _uiState.update { it.copy(showEditChatDialog = true) }
    }

    fun hideEditChatDialog() {
        _uiState.update { it.copy(showEditChatDialog = false) }
    }

    /**
     * Update the current group's name and (optionally) avatar. The backend
     * gates this on OWNER/ADMIN. On success we patch the loaded chat + list
     * item so the details screen and chat list reflect the change immediately.
     */
    fun editGroupChat(
        name: String,
        imageBytes: ByteArray? = null,
        imageFileName: String? = null,
        imageContentType: String? = null,
    ) {
        val chatId = _uiState.value.selectedChatId ?: return
        _uiState.update { it.copy(savingGroup = true) }
        viewModelScope.launch {
            chatRepository.updateGroupChat(chatId, UpdateGroupChatRequest(name = name.trim()))
                .onSuccess { updated ->
                    var imageUrl = updated.imageUrl
                    if (imageBytes != null && imageFileName != null && imageContentType != null) {
                        chatRepository.uploadChatImage(chatId, imageBytes, imageFileName, imageContentType)
                            .onSuccess { imageUrl = it.imageUrl }
                    }
                    _uiState.update { state ->
                        val mergedChat = state.selectedChat?.copy(name = updated.name, imageUrl = imageUrl)
                            ?: updated.copy(imageUrl = imageUrl)
                        state.copy(
                            selectedChat = mergedChat,
                            chats = state.chats.map { c ->
                                if (c.id == chatId) c.copy(name = updated.name, imageUrl = imageUrl) else c
                            },
                            showEditChatDialog = false,
                            savingGroup = false,
                        )
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(error = e.message, savingGroup = false) } }
        }
    }

    // ── Invite members ──

    fun showInviteDialog() {
        _uiState.update {
            it.copy(
                showInviteDialog = true,
                followingLoading = true,
                following = emptyList(),
                followingPage = 0,
                followingHasMore = true,
                followingLoadingMore = false,
                followingSearchQuery = "",
            )
        }
        loadFollowing()
    }

    fun hideInviteDialog() {
        followingSearchJob?.cancel()
        followingSearchJob = null
        _uiState.update {
            it.copy(
                showInviteDialog = false,
                following = emptyList(),
                followingPage = 0,
                followingHasMore = true,
                followingLoading = false,
                followingLoadingMore = false,
                followingSearchQuery = "",
            )
        }
    }

    /** Invite the chosen users to the current group, then refetch the chat so
     *  the member list reflects the new members. */
    fun inviteMembers(userIds: List<String>) {
        val chatId = _uiState.value.selectedChatId ?: return
        if (userIds.isEmpty()) return
        _uiState.update { it.copy(invitingMembers = true) }
        viewModelScope.launch {
            chatRepository.inviteToChat(chatId, InviteToChatRequest(userIds = userIds))
                .onSuccess {
                    chatRepository.getChat(chatId).onSuccess { fresh ->
                        _uiState.update { it.copy(selectedChat = fresh) }
                    }
                    _uiState.update {
                        it.copy(showInviteDialog = false, invitingMembers = false, following = emptyList())
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(error = e.message, invitingMembers = false) } }
        }
    }

    // ── Chat actions ──

    fun toggleChatInfo() {
        _uiState.update { it.copy(showChatInfo = !it.showChatInfo) }
    }

    fun closeChatInfo() {
        _uiState.update { it.copy(showChatInfo = false) }
    }

    fun toggleChatMenu() {
        _uiState.update { it.copy(showChatMenu = !it.showChatMenu) }
    }

    fun closeChatMenu() {
        _uiState.update { it.copy(showChatMenu = false) }
    }

    fun leaveChat() {
        val chatId = _uiState.value.selectedChatId ?: return
        _uiState.update { it.copy(actionInProgress = true, showChatMenu = false) }
        viewModelScope.launch {
            chatRepository.leaveChat(chatId)
                .onSuccess {
                    wsClient.disconnect()
                    _uiState.update {
                        it.copy(
                            selectedChatId = null,
                            selectedChat = null,
                            messages = emptyList(),
                            actionInProgress = false,
                            showChatInfo = false
                        )
                    }
                    loadChats()
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, actionInProgress = false) }
                }
        }
    }

    fun muteChat() {
        val chatId = _uiState.value.selectedChatId ?: return
        val chat = _uiState.value.chats.find { it.id == chatId }
        val currentlyMuted = chat?.isMuted ?: false

        _uiState.update { it.copy(actionInProgress = true, showChatMenu = false) }
        viewModelScope.launch {
            chatRepository.muteChat(chatId, MuteChatRequest(mute = !currentlyMuted))
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            chats = state.chats.map {
                                if (it.id == chatId) it.copy(isMuted = !currentlyMuted) else it
                            },
                            actionInProgress = false
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, actionInProgress = false) }
                }
        }
    }

    // ── Message search ──

    fun toggleMessageSearch() {
        _uiState.update {
            it.copy(
                showMessageSearch = !it.showMessageSearch,
                messageSearchQuery = "",
                messageSearchResults = emptyList()
            )
        }
    }

    fun updateSearchQuery(query: String) {
        _uiState.update { it.copy(messageSearchQuery = query) }
        if (query.length >= 2) {
            searchMessages(query)
        } else {
            _uiState.update { it.copy(messageSearchResults = emptyList()) }
        }
    }

    private fun searchMessages(query: String) {
        val chatId = _uiState.value.selectedChatId ?: return
        _uiState.update { it.copy(messageSearchLoading = true) }
        viewModelScope.launch {
            chatRepository.searchMessages(chatId, mapOf("query" to query, "size" to "20"))
                .onSuccess { result ->
                    _uiState.update {
                        it.copy(messageSearchResults = result.messages, messageSearchLoading = false)
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, messageSearchLoading = false) }
                }
        }
    }

    // ── Invite members ──

    fun inviteToChat(userIds: List<String>) {
        val chatId = _uiState.value.selectedChatId ?: return
        viewModelScope.launch {
            chatRepository.inviteToChat(chatId, InviteToChatRequest(userIds = userIds))
                .onSuccess {
                    _uiState.update { it.copy(showChatMenu = false) }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message) }
                }
        }
    }

    /** Open the remove-confirmation popup for [userId] (looked up in the chat). */
    fun requestRemoveMember(userId: String) {
        val member = _uiState.value.selectedChat?.members?.firstOrNull { it.userId == userId } ?: return
        _uiState.update { it.copy(memberPendingRemoval = member) }
    }

    fun cancelRemoveMember() {
        _uiState.update { it.copy(memberPendingRemoval = null) }
    }

    /** Confirm removal — close the popup and kick the member in the background. */
    fun confirmRemoveMember() {
        val userId = _uiState.value.memberPendingRemoval?.userId ?: return
        _uiState.update { it.copy(memberPendingRemoval = null) }
        removeMember(userId)
    }

    /**
     * Remove a member from the current group chat. The backend gates this on
     * OWNER/ADMIN, so callers must still hide the affordance for non-admins —
     * this is the UI-side mirror of that rule. On success we drop the member
     * from the loaded chat so the list updates without a refetch.
     */
    fun removeMember(userId: String) {
        val chatId = _uiState.value.selectedChatId ?: return
        viewModelScope.launch {
            chatRepository.kickMember(chatId, userId)
                .onSuccess {
                    _uiState.update { state ->
                        val updated = state.selectedChat?.let { chat ->
                            chat.copy(members = chat.members.filterNot { it.userId == userId })
                        }
                        state.copy(selectedChat = updated)
                    }
                }
                .onFailure { e -> _uiState.update { it.copy(error = e.message) } }
        }
    }

    // ── Error ──

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }

    // ── WebSocket ──

    private fun connectWebSocket(chatId: String) {
        wsCollectorJob?.cancel()
        wsClient.connect(chatId)

        wsCollectorJob = viewModelScope.launch {
            wsClient.events.collect { event ->
                handleWsEvent(event)
            }
        }
    }

    private fun handleWsEvent(event: ChatWsEvent) {
        when (event) {
            is ChatWsEvent.NewMessage -> {
                val currentUserId = _uiState.value.currentUserId
                if (event.message.senderId != currentUserId) {
                    _uiState.update { state ->
                        state.copy(messages = state.messages + event.message)
                    }
                }
                refreshChatList()
                _uiState.value.selectedChatId?.let { markAsRead(it) }
            }

            is ChatWsEvent.MessageDeleted -> {
                _uiState.update { state ->
                    state.copy(messages = state.messages.filter { it.id != event.data.messageId })
                }
            }

            is ChatWsEvent.MessageEdited -> {
                _uiState.update { state ->
                    state.copy(
                        messages = state.messages.map { msg ->
                            if (msg.id == event.data.messageId)
                                msg.copy(content = event.data.content, isEdited = true)
                            else msg
                        }
                    )
                }
            }

            is ChatWsEvent.UserTyping -> {
                val userId = event.data.userId
                val username = event.data.username ?: userId
                _uiState.update { it.copy(typingUsers = it.typingUsers + (userId to username)) }
                typingClearJobs[userId]?.cancel()
                typingClearJobs[userId] = viewModelScope.launch {
                    delay(3000)
                    _uiState.update { it.copy(typingUsers = it.typingUsers - userId) }
                }
            }

            is ChatWsEvent.UserOnline -> {
                _uiState.update { it.copy(onlineUserIds = it.onlineUserIds + event.data.userId) }
            }

            is ChatWsEvent.UserOffline -> {
                _uiState.update { it.copy(onlineUserIds = it.onlineUserIds - event.data.userId) }
            }

            is ChatWsEvent.MemberKicked -> {
                val currentUserId = _uiState.value.currentUserId
                if (event.data.userId == currentUserId) {
                    _uiState.update {
                        it.copy(selectedChatId = null, selectedChat = null, messages = emptyList())
                    }
                    wsClient.disconnect()
                    loadChats()
                }
            }

            is ChatWsEvent.MemberJoined -> {
                // Refresh chat detail to update members list
                _uiState.value.selectedChatId?.let { chatId ->
                    viewModelScope.launch {
                        chatRepository.getChat(chatId)
                            .onSuccess { chat -> _uiState.update { it.copy(selectedChat = chat) } }
                    }
                }
            }

            is ChatWsEvent.MemberLeft -> {
                _uiState.value.selectedChatId?.let { chatId ->
                    viewModelScope.launch {
                        chatRepository.getChat(chatId)
                            .onSuccess { chat -> _uiState.update { it.copy(selectedChat = chat) } }
                    }
                }
            }

            is ChatWsEvent.ReactionAdded -> {
                _uiState.update { state ->
                    state.copy(
                        messages = state.messages.map { msg ->
                            if (msg.id == event.data.messageId) {
                                val existing = msg.reactions.find { it.emoji == event.data.emoji }
                                val newReactions = if (existing != null) {
                                    msg.reactions.map {
                                        if (it.emoji == event.data.emoji && !it.userIds.contains(event.data.userId))
                                            it.copy(count = it.count + 1, userIds = it.userIds + event.data.userId)
                                        else it
                                    }
                                } else {
                                    msg.reactions + ReactionDto(emoji = event.data.emoji, count = 1, userIds = listOf(event.data.userId))
                                }
                                msg.copy(reactions = newReactions)
                            } else msg
                        }
                    )
                }
            }

            is ChatWsEvent.ReactionRemoved -> {
                _uiState.update { state ->
                    state.copy(
                        messages = state.messages.map { msg ->
                            if (msg.id == event.data.messageId) {
                                val newReactions = msg.reactions.mapNotNull { reaction ->
                                    if (reaction.emoji == event.data.emoji) {
                                        val newUserIds = reaction.userIds - event.data.userId
                                        if (newUserIds.isEmpty()) null
                                        else reaction.copy(count = newUserIds.size, userIds = newUserIds)
                                    } else reaction
                                }
                                msg.copy(reactions = newReactions)
                            } else msg
                        }
                    )
                }
            }

            is ChatWsEvent.Connected, is ChatWsEvent.Disconnected -> {
                // No-op
            }
        }
    }

    // ── Private helpers ──

    private fun markAsRead(chatId: String) {
        viewModelScope.launch {
            chatRepository.markAsRead(chatId)
            _uiState.update { state ->
                state.copy(
                    chats = state.chats.map { chat ->
                        if (chat.id == chatId) chat.copy(unreadCount = 0) else chat
                    }
                )
            }
        }
    }

    /** Background WebSocket-driven refresh — re-fetches page 1 with the
     *  current search so the visible list stays in sync with the server.
     *  Anything past page 1 has to be re-paged by the user; the alternative
     *  (re-fetching everything they'd scrolled to) would be wasteful and
     *  surprising on long lists.
     */
    private fun refreshChatList() {
        viewModelScope.launch {
            val search = _uiState.value.chatsSearchQuery
            chatRepository.getChats(chatListParams(page = 1, search = search))
                .onSuccess { page ->
                    if (_uiState.value.chatsSearchQuery != search) return@onSuccess
                    _uiState.update {
                        it.copy(
                            chats = page.items,
                            chatsPage = page.page,
                            chatsHasMore = page.page < page.totalPages,
                            chatsTotal = page.totalItems,
                        )
                    }
                }
        }
    }
}




